import type { VoiceOption } from '@bingo/shared';

/**
 * Fala pelo `speechSynthesis`, contornando os problemas reais da API.
 *
 * A narração não funcionava em produção por três motivos que se somam, e
 * nenhum deles dá erro — todos falham **em silêncio**:
 *
 * 1. **Autoplay.** Sem um gesto do usuário, `speak()` é aceito e não sai som
 *    nenhum. Por isso o aquecimento tem de acontecer DENTRO do handler do
 *    clique (`unlock()`), não depois de um `await`.
 * 2. **Vozes assíncronas.** `getVoices()` devolve `[]` no primeiro acesso em
 *    Chrome e Safari; a lista só chega no evento `voiceschanged`. Quem escolhe
 *    a voz cedo demais fica com `undefined` e o navegador usa a voz do
 *    sistema — frequentemente em inglês, lendo "Bê" como "bee".
 * 3. **Bugs do Chrome.** A fila trava depois de ~15s de fala e após
 *    `pause()`/troca de aba; `cancel()` antes de cada fala e um `resume()`
 *    periódico são os contornos conhecidos. `onend` também não é confiável,
 *    então nada aqui depende dele para encadear.
 */

const synthesis: SpeechSynthesis | undefined =
  typeof window === 'undefined' ? undefined : window.speechSynthesis;

/** Quanto esperar pelo `voiceschanged` antes de desistir. */
const VOICES_TIMEOUT_MS = 3000;
/** Intervalo do `resume()` que mantém a fila do Chrome viva. */
const KEEPALIVE_MS = 5000;

export function hasSynthesisApi(): boolean {
  return typeof synthesis !== 'undefined';
}

function listVoices(): SpeechSynthesisVoice[] {
  try {
    return synthesis?.getVoices() ?? [];
  } catch {
    return [];
  }
}

/**
 * Espera as vozes aparecerem.
 *
 * Resolve assim que houver qualquer voz, ou no timeout — **nunca fica
 * pendurado**, porque num aparelho sem voz nenhuma o `voiceschanged` pode não
 * disparar e a tela ficaria esperando para sempre.
 */
export function loadVoices(timeoutMs = VOICES_TIMEOUT_MS): Promise<SpeechSynthesisVoice[]> {
  const immediate = listVoices();
  if (immediate.length > 0) return Promise.resolve(immediate);
  if (!synthesis) return Promise.resolve([]);

  return new Promise((resolve) => {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      synthesis.removeEventListener('voiceschanged', onChange);
      window.clearTimeout(timer);
      window.clearInterval(poll);
      resolve(listVoices());
    };

    const onChange = () => finish();
    synthesis.addEventListener('voiceschanged', onChange);

    // Alguns navegadores populam a lista sem nunca disparar o evento.
    const poll = window.setInterval(() => {
      if (listVoices().length > 0) finish();
    }, 250);

    const timer = window.setTimeout(finish, timeoutMs);
  });
}

export function toVoiceOptions(voices: SpeechSynthesisVoice[]): VoiceOption[] {
  return voices.map((voice) => ({ voiceURI: voice.voiceURI, name: voice.name, lang: voice.lang }));
}

export function portugueseVoices(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice[] {
  return voices.filter((voice) => /^pt/i.test(voice.lang));
}

/**
 * Escolhe a voz: a pedida, senão a primeira pt-BR, senão qualquer pt.
 * Devolve `null` quando não há voz em português — caso em que o telão reporta
 * `NO_PT_VOICE` e quem opera passa a narrar pelo celular.
 */
export function pickVoice(
  voices: SpeechSynthesisVoice[],
  preferredUri?: string | null,
): SpeechSynthesisVoice | null {
  const portuguese = portugueseVoices(voices);

  if (preferredUri) {
    const exact = voices.find((voice) => voice.voiceURI === preferredUri);
    if (exact) return exact;
  }

  return portuguese.find((voice) => /pt[-_]BR/i.test(voice.lang)) ?? portuguese[0] ?? null;
}

let audioContext: AudioContext | null = null;
let keepAlive: number | null = null;

/**
 * Libera o áudio. **Tem de ser chamado DENTRO do handler do gesto**: o
 * navegador olha a pilha de chamadas, e qualquer `await` antes disto já
 * descarta a permissão.
 *
 * Devolve se o desbloqueio funcionou, para a tela poder reportar em vez de
 * fingir que está tudo bem.
 */
export function unlockAudio(): { audioContext: boolean; speech: boolean } {
  let contextOk = false;

  try {
    audioContext ??= new (
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();
    void audioContext.resume();
    contextOk = audioContext.state !== 'suspended';
  } catch {
    contextOk = false;
  }

  let speechOk = false;

  if (synthesis) {
    try {
      synthesis.cancel();
      // Fala de aquecimento: curta e inaudível, só para o gesto valer.
      const warmup = new SpeechSynthesisUtterance(' ');
      warmup.volume = 0;
      warmup.rate = 2;
      synthesis.speak(warmup);
      speechOk = true;
    } catch {
      speechOk = false;
    }
  }

  return { audioContext: contextOk, speech: speechOk };
}

/**
 * Que tipo de fala é esta. Define quem espera por quem.
 *
 * `announcement` é o anúncio de rodada, de ganhador, de fim de partida —
 * fala longa, que marca uma virada do jogo. `ball` é o número sorteado.
 */
export type SpeechKind = 'ball' | 'announcement';

export interface SpeakOptions {
  voice?: SpeechSynthesisVoice | null;
  rate?: number;
  kind?: SpeechKind;
}

/**
 * Quanto a bola espera o anúncio terminar antes de atropelá-lo.
 *
 * Dois motivos para o limite existir e para ele ser curto:
 *
 *  · `onend` não é confiável no Chrome (o mesmo motivo do keep-alive). Sem
 *    limite, uma fala que nunca avisa que acabou deixaria a bola presa para
 *    sempre.
 *  · o anúncio mais longo ("Rodada 4. Valendo Quatro cantos. Continuamos com
 *    as bolas já sorteadas.") passa de cinco segundos, e a bola já está no
 *    painel esperando ser cantada. Depois de 2,5s o número vale mais do que
 *    o resto da frase.
 *
 * **A bola nunca é descartada por causa da espera**: ou o anúncio termina e
 * ela sai, ou ela o interrompe.
 */
const ANNOUNCEMENT_WAIT_MS = 2500;

/** O que está falando agora, se é que algo está. */
let speakingKind: SpeechKind | null = null;
/** Bola esperando o anúncio acabar. Só a MAIS RECENTE interessa. */
let queuedBall: { text: string; options: SpeakOptions } | null = null;
let waitTimer: number | null = null;

function clearWaitTimer(): void {
  if (waitTimer === null) return;
  window.clearTimeout(waitTimer);
  waitTimer = null;
}

/** Fala a bola que estava esperando, se ainda houver alguma. */
function flushQueuedBall(): void {
  clearWaitTimer();

  const next = queuedBall;
  queuedBall = null;
  if (next) speakNow(next.text, next.options, 'ball');
}

function speakNow(text: string, options: SpeakOptions, kind: SpeechKind): void {
  if (!synthesis) return;

  try {
    // Cancelar antes de cada fala é contorno conhecido do Chrome (a fila
    // acumula e trava). Aqui também é a regra do jogo: o que importa é a
    // ÚLTIMA bola, não a anterior.
    synthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = options.voice?.lang ?? 'pt-BR';
    utterance.rate = options.rate ?? 0.95;
    if (options.voice) utterance.voice = options.voice;

    speakingKind = kind;

    const finish = () => {
      if (speakingKind === kind) speakingKind = null;
      flushQueuedBall();
    };

    utterance.onend = finish;
    utterance.onerror = finish;

    synthesis.speak(utterance);
    startKeepAlive();
  } catch {
    // Narração é enfeite: falhar aqui não pode derrubar o telão.
    speakingKind = null;
  }
}

/**
 * Fala um texto. Silencioso e sem lançar quando não há síntese.
 *
 * A bola que chega durante um anúncio **espera** em vez de ser descartada.
 * Cenário real: "Rodada 4. Valendo cartela cheia." ainda está no ar quando o
 * operador sorteia, e o `cancel()` de cada fala fazia uma das duas sumir —
 * dependendo de milissegundos, às vezes o número.
 */
export function speak(text: string, options: SpeakOptions = {}): void {
  if (!synthesis || !text.trim()) return;

  const kind = options.kind ?? 'ball';
  const busy = speakingKind === 'announcement' && (synthesis.speaking || synthesis.pending);

  if (kind === 'ball' && busy) {
    // Bola anterior ainda na fila é substituída pela atual: ninguém quer
    // ouvir a penúltima bola com atraso.
    queuedBall = { text, options };

    clearWaitTimer();
    waitTimer = window.setTimeout(flushQueuedBall, ANNOUNCEMENT_WAIT_MS);
    return;
  }

  // Um anúncio NÃO descarta a bola que está esperando: ela continua na fila
  // e sai quando ele terminar. Descartar aqui foi um defeito real — a
  // primeira bola de uma rodada que herdou bolas sumia porque o anúncio
  // seguinte limpava a fila antes de ela ser cantada.
  if (kind === 'announcement' && queuedBall) {
    clearWaitTimer();
    waitTimer = window.setTimeout(flushQueuedBall, ANNOUNCEMENT_WAIT_MS);
    speakNow(text, options, kind);
    return;
  }

  queuedBall = null;
  clearWaitTimer();
  speakNow(text, options, kind);
}

/**
 * Descarta a bola que estava esperando.
 *
 * Para quando o painel muda de verdade — rodada nova, partida nova. A bola
 * em espera pertence a um painel que não existe mais; cantá-la depois seria
 * anunciar um número que ninguém mais vê na tela.
 */
export function dropQueuedSpeech(): void {
  queuedBall = null;
  clearWaitTimer();
}

export function cancelSpeech(): void {
  queuedBall = null;
  clearWaitTimer();
  speakingKind = null;

  try {
    synthesis?.cancel();
  } catch {
    // nada a cancelar
  }
}

/**
 * `resume()` periódico enquanto há fala pendente.
 *
 * Bug conhecido do Chrome: a fila congela sozinha depois de alguns segundos,
 * e o `resume()` a destrava. Não custa nada quando não há nada falando.
 */
function startKeepAlive(): void {
  if (keepAlive !== null || !synthesis) return;

  keepAlive = window.setInterval(() => {
    if (!synthesis.speaking && !synthesis.pending) {
      window.clearInterval(keepAlive!);
      keepAlive = null;
      return;
    }
    try {
      synthesis.resume();
    } catch {
      // segue
    }
  }, KEEPALIVE_MS);
}
